import { ApolloClient, InMemoryCache, HttpLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import type { NhostClient } from '@nhost/nhost-js';

/**
 * Cria Apollo Client integrado com Nhost.
 * - Pega URL do GraphQL via nhost.graphql.httpUrl
 * - Anexa JWT do usuario logado em cada request
 *
 * O modo "customer" (x-hasura-role: customer + x-hasura-access-token, pra
 * consultar reserva por link sem login) foi removido em 2026-08-05: o Hasura so
 * aceita x-hasura-role que esteja nos allowed roles do JWT, e `customer` nunca
 * esteve — a query sempre voltava vazia. Reserva se consulta logado, em /profile.
 */
export function createApolloClient(nhost: NhostClient) {
  const httpLink = new HttpLink({
    uri: () => nhost.graphql.httpUrl,
  });

  const authLink = setContext(async (_operation, { headers }) => {
    const session = nhost.auth.getSession();
    const token = session?.accessToken;
    const customHeaders = (headers as Record<string, string>) ?? {};

    // Em rotas /admin, força role admin (cobre caso de default_role !== admin no JWT).
    // Só envia se a sessão realmente tiver o papel — o Hasura rejeita um
    // x-hasura-role fora dos allowed roles do JWT e a query inteira falha.
    const isAdminRoute =
      typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');
    const hasAdminRole = session?.user?.roles?.includes('admin') ?? false;
    const adminRoleHeader =
      isAdminRoute && hasAdminRole && !customHeaders['x-hasura-role']
        ? { 'x-hasura-role': 'admin' }
        : {};

    return {
      headers: {
        ...customHeaders,
        ...adminRoleHeader,
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    };
  });

  return new ApolloClient({
    link: from([authLink, httpLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: { errorPolicy: 'all' },
      query: { errorPolicy: 'all' },
    },
  });
}
